import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// Money In: the EOB list (PR #72 update, 2026-11-01) - SHADOW and DARK.
// The EOBs the bot read, newest first; each opens its review page (BillingRemit.jsx). This table used to
// sit at the bottom of the Billing home, under the chain; it has its own page now, so the home is the
// overview and Money In is where the EOB work starts. Same reads as before (GET /billing/remits),
// behind the server's billing gate like every billing page: anyone else is sent home.
// The URL carries the portal's EOB id only. Nothing here changes Raintree.

const PAGE = 50;

function money(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDay(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

export default function BillingMoneyIn() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    setError("");
    api
      .get("/billing/remits", { params: { offset, limit: PAGE } })
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 503
        ? "Money In isn't set up yet (billing patch 012)."
        : "Couldn't load the EOBs."));
    return () => {
      live = false;
    };
  }, [allowed, offset]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;

  const remits = data?.remits || [];
  return (
    <div className="p-8 max-w-6xl" data-testid="billing-money-in">
      <BillingNav current="eob" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>{" / "}Money In
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">Money In: EOBs</h1>
        {data && (
          <span className="text-sm text-slate-500" data-testid="billing-remits-total">
            {data.total.toLocaleString()} EOBs
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500" data-testid="billing-money-in-note">
        Shadow mode. The EOBs and payment lists the bot read, newest first. Open one to see each line matched to its
        charge; proposed postings wait for a person to approve them. Approving writes billing postings for review only;
        nothing here changes Raintree.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</div>
      )}
      {!error && !data && <div className="mt-6 text-sm text-slate-500">Loading the EOBs…</div>}
      {!error && data && remits.length === 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">
          Nothing to review yet.
        </div>
      )}
      {remits.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm" data-testid="billing-remits">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">EOB</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 text-right">Lines</th>
                <th className="px-4 py-3 text-right">Open</th>
                <th className="px-4 py-3 text-right">Proposed</th>
                <th className="px-4 py-3 text-right">Approved</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {remits.map((r) => (
                <tr key={r.remit_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-800">#{r.remit_id}</td>
                  <td className="px-4 py-2 text-slate-600">{usDay(r.received_at)}</td>
                  <td className="px-4 py-2 text-slate-600">{r.remit_type === "payment_list" ? "Payment list" : "EOB"}</td>
                  <td className="px-4 py-2 text-right">{r.lines}</td>
                  <td className="px-4 py-2 text-right">{r.open}</td>
                  <td className="px-4 py-2 text-right">{r.proposed}</td>
                  <td className="px-4 py-2 text-right">{r.approved}</td>
                  <td className="px-4 py-2 text-right">{money(r.paid)}</td>
                  <td className="px-4 py-2 text-right">
                    <Link to={`/billing/eob/${r.remit_id}`} className="font-medium text-sky-700 hover:underline"
                          data-testid={`billing-open-${r.remit_id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > PAGE && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40"
                  disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Newer
          </button>
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40"
                  disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>
            Older
          </button>
          <span className="text-slate-500">{offset + 1}–{Math.min(offset + PAGE, data.total)}</span>
        </div>
      )}
    </div>
  );
}
